/** @file Inserting a vertex outside the mesh: a new hull vertex past the nearest visible hull edge. */
import { flatOrient } from '@pose2d/autorig/mesh/flatOrient';
import { ringArea } from '@pose2d/autorig/mesh/ringArea';
import { ringIsSimple } from '@pose2d/autorig/mesh/ringIsSimple';
import { segmentDistance } from '@pose2d/autorig/mesh/segmentDistance';
import type { MeshAtt } from '@pose2d/format/types';
import type { TopologyPlan, VertexSource } from './blendSources';
import { insertParts } from './insertParts';

/** The hull ring of `q` (points of `p` then the new one at index n), the new vertex spliced in after hull vertex `a`. */
function ringWith(q: ArrayLike<number>, hull: number, a: number, n: number): number[] {
  const ring: number[] = [];
  for (let v = 0; v < hull; v++) {
    ring.push(q[2 * v] ?? 0, q[2 * v + 1] ?? 0);
    if (v === a) ring.push(q[2 * n] ?? 0, q[2 * n + 1] ?? 0);
  }
  return ring;
}

/** The plan over hull edge a → a+1 when (x, y) lies strictly on its outer side and the grown outline stays simple. */
function tryEdge(mesh: MeshAtt, q: ArrayLike<number>, n: number, a: number, oldArea: number): TopologyPlan | null {
  const b = (a + 1) % mesh.hull;
  for (let t = 0; t < mesh.triangles.length; t += 3) {
    const tri = insertParts.triAt(mesh.triangles, t);
    const s = insertParts.sideOf(tri, a, b);
    if (s === null) continue;
    if (flatOrient(q, a, b, n) * flatOrient(q, a, b, s.c) >= 0) return null;
    const ring = ringWith(q, mesh.hull, a, n);
    if (!ringIsSimple(ring) || ringArea(ring) * oldArea <= 0) return null;
    const w = insertParts.barycentric(q, tri, q[2 * n] ?? 0, q[2 * n + 1] ?? 0);
    if (w === null) return null;
    const source: VertexSource = { kind: 'blend', parts: [[tri[0], w[0]], [tri[1], w[1]], [tri[2], w[2]]] };
    const slot = insertParts.hullSlot(a, b, mesh.hull) ?? mesh.hull;
    return insertParts.planWith(mesh, slot, source, [...mesh.triangles, s.w, s.u, -1], true);
  }
  return null;
}

/**
 * Plan for world (x, y) outside the mesh (world points `p`): hull edges are tried by distance to the segment; the
 * first one that sees the point (strictly outside, past its triangle's third corner) and keeps the outline simple and
 * its winding gets triangle (w, u, new), the vertex extrapolated over that triangle. Null when no edge qualifies.
 */
export function extendHullPlan(mesh: MeshAtt, p: ArrayLike<number>, x: number, y: number): TopologyPlan | null {
  const n = mesh.uvs.length / 2;
  const q = [...Array.from(p).slice(0, 2 * n), x, y];
  const oldArea = ringArea(q.slice(0, 2 * mesh.hull));
  const at = (v: number, k: 0 | 1): number => q[2 * v + k] ?? 0;
  const order = Array.from({ length: mesh.hull }, (_, a) => {
    const b = (a + 1) % mesh.hull;
    return { a, d: segmentDistance(x, y, at(a, 0), at(a, 1), at(b, 0), at(b, 1)) };
  }).sort((l, r) => l.d - r.d);
  for (const { a } of order) {
    const plan = tryEdge(mesh, q, n, a, oldArea);
    if (plan !== null) return plan;
  }
  return null;
}
